import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db } from './firebase';
import { updateProfile, sendPasswordResetEmail, deleteUser, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, deleteDoc, setDoc, getDoc, updateDoc, query, where, getDocs, collection } from 'firebase/firestore';
import { logout } from './auth';

const AccountManagement = () => {
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [message, setMessage] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [adminLeagues, setAdminLeagues] = useState([]);
  const [memberLeagues, setMemberLeagues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(null);
  const [showDeleteLeagueConfirmation, setShowDeleteLeagueConfirmation] = useState(null);
  const [messageSection, setMessageSection] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setDisplayName(currentUser.displayName || '');
        fetchUserLeagues(currentUser.uid);
      } else {
        setUser(null);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserLeagues = async (userId) => {
    setIsLoading(true);
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnapshot = await getDoc(userDocRef);
      const userData = userDocSnapshot.data();

      const adminLeagueIds = userData.admin_leagues || [];
      const memberLeagueIds = [
        ...(userData.in_match_predictor_leagues || []),
        ...(userData.in_table_predictor_leagues || [])
      ];

      const adminLeaguesData = await Promise.all(
        adminLeagueIds.map(async (leagueId) => {
          const leagueDocRef = doc(db, 'leagues', leagueId);
          const leagueDocSnapshot = await getDoc(leagueDocRef);
          return { id: leagueId, ...leagueDocSnapshot.data() };
        })
      );

      const memberLeaguesData = await Promise.all(
        memberLeagueIds.map(async (leagueId) => {
          const leagueDocRef = doc(db, 'leagues', leagueId);
          const leagueDocSnapshot = await getDoc(leagueDocRef);
          return { id: leagueId, ...leagueDocSnapshot.data() };
        })
      );

      setAdminLeagues(adminLeaguesData);
      setMemberLeagues(memberLeaguesData.filter(league => !adminLeagueIds.includes(league.id)));
    } catch (error) {
      console.error("Error fetching user leagues:", error);
      setMessage("Error loading leagues. Please try again.");
      setMessageSection('Leagues');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisplayNameChange = async (e) => {
    e.preventDefault();

    if (displayName === user.displayName) {
      return;
    }

    try {
      await updateProfile(auth.currentUser, { displayName });
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, { display_name: displayName }, { merge: true });
      setMessage('Display name successfully updated to: ' + displayName);
      setMessageSection('Profile');
    } catch (error) {
      setMessage(`Error updating display name: ${error.message}`);
      setMessageSection('Profile');
    }
  };

  const handlePasswordReset = async () => {
    try {
      await sendPasswordResetEmail(auth, user.email);
      setMessage('Password reset email sent. Check your inbox.');
      setMessageSection('Profile');
    } catch (error) {
      setMessage(`Error sending password reset email: ${error.message}`);
      setMessageSection('Profile');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation === user.email) {
      try {
        await signInWithEmailAndPassword(auth, user.email, password);
        await deleteAccountInfoFromDb();
        const userDocRef = doc(db, 'users', user.uid);
        await deleteDoc(userDocRef);
        await deleteUser(auth.currentUser);
        setMessage('Your account has been deleted.');
        setMessageSection('Profile');
        setShowDeleteConfirmation(false);
      } catch (error) {
        setMessage(`Error deleting account: ${error.message}`);
        setMessageSection('Profile');
      }
    } else {
      setMessage('Please enter your email correctly to delete your account.');
      setMessageSection('Profile');
    }
  };

  const deleteAccountInfoFromDb = async () => {
    const userDocRef = doc(db, 'users', user.uid);
    const userDocSnapshot = await getDoc(userDocRef);
    const userData = userDocSnapshot.data();
    
    const userAdminLeagues = userData.admin_leagues || [];
    const inMatchPredictorLeagues = userData.in_match_predictor_leagues || [];
    const inTablePredictorLeagues = userData.in_table_predictor_leagues || [];

    for (const leagueId of [...inMatchPredictorLeagues, ...inTablePredictorLeagues]) {
      const leagueDocRef = doc(db, 'leagues', leagueId);
      const leagueDocSnapshot = await getDoc(leagueDocRef);
      const leagueData = leagueDocSnapshot.data();
      const updatedMembers = leagueData.members.filter(member => member !== user.uid) || [];
      await updateDoc(leagueDocRef, { members: updatedMembers });
    }

    for (const leagueId of userAdminLeagues) {
        const leagueDocRef = doc(db, 'leagues', leagueId);
        const leagueDocSnapshot = await getDoc(leagueDocRef);
        const leagueData = leagueDocSnapshot.data();
        const leagueMembers = leagueData.members || [];
        for (const member of leagueMembers) {
            const userDocRef = doc(db, 'users', member);
            const userDocSnapshot = await getDoc(userDocRef);
            const userData = userDocSnapshot.data();
            const updatedInMatchPredictorLeagues = userData.in_match_predictor_leagues.filter(league => league !== leagueId) || [];
            const updatedInTablePredictorLeagues = userData.in_table_predictor_leagues.filter(league => league !== leagueId) || [];
            await updateDoc(userDocRef, { in_match_predictor_leagues: updatedInMatchPredictorLeagues, in_table_predictor_leagues: updatedInTablePredictorLeagues });
        }
    }

    for (const leagueId of userAdminLeagues) {
        const leagueDocRef = doc(db, 'leagues', leagueId);
        await deleteDoc(leagueDocRef);
    }      

    const matchPredictionsQuery = query(collection(db, 'match_predictions'), where('user_id', '==', user.uid));
    const tablePredictionsQuery = query(collection(db, 'table_predictions'), where('user_id', '==', user.uid));
    const matchPredictionsSnapshot = await getDocs(matchPredictionsQuery);
    const tablePredictionsSnapshot = await getDocs(tablePredictionsQuery);
    for (const matchPredictionDoc of matchPredictionsSnapshot.docs) {
      await deleteDoc(matchPredictionDoc.ref);
    }
    for (const tablePredictionDoc of tablePredictionsSnapshot.docs) {
      await deleteDoc(tablePredictionDoc.ref);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setMessage('You have been signed out.');
      setMessageSection('Profile');
    } catch (error) {
      setMessage(`Error signing out: ${error.message}`);
      setMessageSection('Profile');
    }
  };

  const handleDeleteLeague = async (leagueId) => {
    try {
      const leagueDocRef = doc(db, 'leagues', leagueId);
      const leagueDocSnapshot = await getDoc(leagueDocRef);
      const leagueData = leagueDocSnapshot.data();
      const leagueMembers = leagueData.members || [];

      for (const memberId of leagueMembers) {
        const memberDocRef = doc(db, 'users', memberId);
        const memberDocSnapshot = await getDoc(memberDocRef);
        const memberData = memberDocSnapshot.data();
        const updatedInMatchPredictorLeagues = memberData.in_match_predictor_leagues.filter(id => id !== leagueId) || [];
        const updatedInTablePredictorLeagues = memberData.in_table_predictor_leagues.filter(id => id !== leagueId) || [];
        await updateDoc(memberDocRef, {
          in_match_predictor_leagues: updatedInMatchPredictorLeagues,
          in_table_predictor_leagues: updatedInTablePredictorLeagues
        });
      }

      await deleteDoc(leagueDocRef);

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnapshot = await getDoc(userDocRef);
      const userData = userDocSnapshot.data();
      const updatedAdminLeagues = userData.admin_leagues.filter(id => id !== leagueId) || [];
      await updateDoc(userDocRef, { admin_leagues: updatedAdminLeagues });

      setAdminLeagues(adminLeagues.filter(league => league.id !== leagueId));
      setMessage('League deleted successfully.');
      setMessageSection('Leagues');
    } catch (error) {
      setMessage(`Error deleting league: ${error.message}`);
      setMessageSection('Leagues');
    }
  };

  const handleLeaveLeague = async (leagueId) => {
    try {
      const leagueDocRef = doc(db, 'leagues', leagueId);
      const leagueDocSnapshot = await getDoc(leagueDocRef);
      const leagueData = leagueDocSnapshot.data();
      const updatedMembers = leagueData.members.filter(memberId => memberId !== user.uid) || [];
      await updateDoc(leagueDocRef, { members: updatedMembers });

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnapshot = await getDoc(userDocRef);
      const userData = userDocSnapshot.data();
      const updatedInMatchPredictorLeagues = userData.in_match_predictor_leagues.filter(id => id !== leagueId) || [];
      const updatedInTablePredictorLeagues = userData.in_table_predictor_leagues.filter(id => id !== leagueId) || [];
      await updateDoc(userDocRef, {
        in_match_predictor_leagues: updatedInMatchPredictorLeagues,
        in_table_predictor_leagues: updatedInTablePredictorLeagues
      });

      setMemberLeagues(memberLeagues.filter(league => league.id !== leagueId));
      setMessage('You have left the league successfully.');
      setMessageSection('Leagues');
    } catch (error) {
      setMessage(`Error leaving league: ${error.message}`);
      setMessageSection('Leagues');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-8 rounded-lg shadow-md"
        >
          <h2 className="text-2xl font-bold mb-4">Please sign in to manage your account.</h2>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-8 rounded-lg shadow-md"
        >
          <h2 className="text-3xl font-bold mb-6 text-center">League Management</h2>
          
          {message && messageSection === 'Leagues' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className={`text-center mb-4 font-bold ${
                message.includes('Error') ? 'text-red-500' : 'text-green-500'
              }`}
            >
              {message}
            </motion.div>
          )}
  
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Admin Leagues</h3>
            {adminLeagues.length > 0 ? (
              <ul className="space-y-4">
                {adminLeagues.map(league => (
                  <li key={league.id} className="flex justify-between items-center bg-gray-100 p-3 rounded-md">
                    <span className="font-medium">{league.name}</span>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowDeleteLeagueConfirmation(league.id)}
                      className="bg-red-500 text-white py-1 px-3 rounded-md hover:bg-red-600 transition duration-300"
                    >
                      Delete
                    </motion.button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600">You are not an admin of any leagues.</p>
            )}
          </div>
  
          <div>
            <h3 className="text-xl font-semibold mb-4">Member Leagues</h3>
            {memberLeagues.length > 0 ? (
              <ul className="space-y-4">
                {memberLeagues.map(league => (
                  <li key={league.id} className="flex justify-between items-center bg-gray-100 p-3 rounded-md">
                    <span className="font-medium">{league.name}</span>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowLeaveConfirmation(league.id)}
                      className="bg-gray-500 text-white py-1 px-3 rounded-md hover:bg-gray-600 transition duration-300"
                    >
                      Leave
                    </motion.button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600">You are not a member of any leagues (that you are not an admin of).</p>
            )}
          </div>
        </motion.div>
  
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white p-8 rounded-lg shadow-md"
        >
          <h2 className="text-3xl font-bold mb-6 text-center">Account Management</h2>

          {message && messageSection === 'Profile' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className={`text-center mb-4 font-bold ${
                message.includes('Error') ? 'text-red-500' : 'text-green-500'
              }`}
            >
              {message}
            </motion.div>
          )}
          
          <form onSubmit={handleDisplayNameChange} className="mb-6">
            <div className="mb-4">
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                maxLength={20}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="w-full bg-premier-purple text-white py-2 px-4 rounded-md hover:bg-purple-600 transition duration-300"
            >
              Update Display Name
            </motion.button>
          </form>
  
          <div className="space-y-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePasswordReset}
              className="w-full bg-premier-pink text-white py-2 px-4 rounded-md hover:bg-pink-600 transition duration-300"
            >
              Reset Password
            </motion.button>
  
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSignOut}
              className="w-full bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition duration-300"
            >
              Logout
            </motion.button>
          </div>
  
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-red-600 mb-4 text-center">Danger Zone</h3>
            {!showDeleteConfirmation ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowDeleteConfirmation(true)}
                className="w-full bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition duration-300"
              >
                Delete Account
              </motion.button>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Warning: Deleting your account is irreversible. All data will be permanently deleted. Any leagues
                  which you are the admin of will be deleted for all players. Please enter your email and password to confirm.
                </p>
                <input
                  type="email"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder={user.email}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmation !== user.email || !password}
                  className={`w-full text-white py-2 px-4 rounded-md transition duration-300 ${
                    deleteConfirmation === user.email && password
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Confirm Account Deletion
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeleteConfirmation('');
                    setPassword('');
                  }}
                  className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-300"
                >
                  Cancel
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
  
      <AnimatePresence>
        {showLeaveConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white p-6 rounded-lg shadow-xl m-4"
            >
              <h3 className="text-xl font-bold mb-4">Confirm Leave League</h3>
              <p className="mb-4">Are you sure you want to leave this league? This action is irreversible.</p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowLeaveConfirmation(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleLeaveLeague(showLeaveConfirmation);
                    setShowLeaveConfirmation(null);
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-300"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
  
      <AnimatePresence>
        {showDeleteLeagueConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white p-6 rounded-lg shadow-xl"
            >
              <h3 className="text-xl font-bold mb-4">Confirm Delete League</h3>
              <p className="mb-4">Are you sure you want to delete this league? This action is irreversible and will remove the league for all players.</p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteLeagueConfirmation(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleDeleteLeague(showDeleteLeagueConfirmation);
                    setShowDeleteLeagueConfirmation(null);
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-300"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccountManagement;